import { LuCreditCard, LuWalletMinimal, LuBanknote, LuHandshake } from 'react-icons/lu';
import type { DonationForm } from '@kuow/core';
import PayPalLogoLight from '../../../assets/images/paypal_logo-light.svg?url';
import PayPalLogoDark from '../../../assets/images/paypal_logo-dark.svg?url';
import type { DonationFormMode, DonationFormPaymentOverrides } from '../types';

export interface BuildPaymentMethodOptionsArgs {
  paymentOverrides: DonationFormPaymentOverrides | undefined;
  formDetails: DonationForm;
  mode: DonationFormMode;
  canMakeApplePay: boolean;
  isMonthly: boolean;
}

/**
 * Builds the list of payment methods PaymentStep offers, already filtered
 * down to the enabled ones.
 *
 * Extracted as a pure function (rather than left inline in the component)
 * so the enable/hide rules -- in particular that the staff-only "Promise
 * (bill later)" option stays hidden unless `paymentOverrides.billLater` is
 * explicitly `true`, and is never offered for monthly donations (a promise
 * is one-time only -- the API rejects `monthly: true` outright) -- can be
 * pinned with a unit test without rendering the full step.
 */
export function buildPaymentMethodOptions({
  paymentOverrides,
  formDetails,
  mode,
  canMakeApplePay,
  isMonthly,
}: BuildPaymentMethodOptionsArgs) {
  const isCreditCardEnabled = paymentOverrides?.creditCard ?? true;
  const isBillLaterEnabled = (paymentOverrides?.billLater ?? false) && !isMonthly;

  return [
    { id: 'card', label: 'Pay with Card', icon: LuCreditCard, enabled: isCreditCardEnabled },
    { id: 'bank', label: 'Pay with Bank Account', icon: LuBanknote, enabled: true },
    { id: 'bill-later', label: 'Promise (bill later)', icon: LuHandshake, enabled: isBillLaterEnabled },
    { id: 'paypal', label: 'Pay with PayPal', displayLabel: 'Pay with', logoLight: PayPalLogoLight, logoDark: PayPalLogoDark, enabled: formDetails.paypal },
    { id: 'apple-pay', label: 'Pay with Apple Pay', icon: LuWalletMinimal, enabled: mode !== 'billing-update' && formDetails.apple_pay && canMakeApplePay },
  ].filter((method) => method.enabled);
}
